import { UserView, VerifyEmailBody, VerifyEmailView } from '@lemoncloud/codes-backend-api';
import { LemonOAuthToken, calcSignature } from '@lemoncloud/lemon-web-core';

import { OAUTH_ENDPOINT, webCore } from '@codes/web-core';

import { LemonRefreshTokenResult, LoginBody, LoginUserResult, UserProfile, VerifyNativeTokenBody } from './types';

const IMAGE_API_ENDPOINT = import.meta.env.VITE_IMAGE_API_ENDPOINT.toLowerCase();

export interface UploadedImage {
    contentLength: number;
    contentType: string;
    createdAt: number;
    hash: string;
    height?: number;
    id: string;
    location: string;
    name: string;
    ns: string;
    origin: string;
    type: string;
    url: string;
    width?: number;
}

const validateNick = (nick: string) => {
    const specialChars = /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/;
    if (specialChars.test(nick)) {
        return false;
    }
    return nick.length < 10;
};

export const updateUserName = async (body: UserView): Promise<UserView> => {
    if (!body.name || !body.id) {
        throw new Error('updateName: .name or .id is required');
    }

    if (!validateNick(body.name)) {
        throw new Error('invalid');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${OAUTH_ENDPOINT}/users/${body.id}`,
        })
        .setBody({ name: body.name })
        .execute<UserView>();

    return data;
};

export const updateProfile = async (body: UserView): Promise<UserView> => {
    if (!body.id) {
        throw new Error('updateProfile: .id is required');
    }

    const { data } = await webCore
        .buildSignedRequest({
            method: 'PUT',
            baseURL: `${OAUTH_ENDPOINT}/users/${body.id}`,
        })
        .setBody({ ...body })
        .execute<UserView>();

    return data;
};

export const fetchUserProfile = async () => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'GET',
            baseURL: `${OAUTH_ENDPOINT}/users/0/profile`,
        })
        .execute<UserProfile>();

    return data;
};

export const createCredentialsByProvider = async (provider: string = 'kakao', code: string) => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${OAUTH_ENDPOINT}/oauth/${provider}/token`,
        })
        .setBody({ code })
        .execute<{ Token: LemonOAuthToken }>();

    return await webCore.buildCredentialsByToken(data.Token);
};

export const requestRefreshToken = async (
    id: string,
    current: string,
    signature: string
): Promise<LemonRefreshTokenResult> => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${OAUTH_ENDPOINT}/oauth/${id}/refresh`,
        })
        .setParams({ token: 1 })
        .setBody({ current, signature })
        .execute<LemonRefreshTokenResult>();

    return data;
};

export const requestLoginUser = async (body: LoginBody) => {
    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${OAUTH_ENDPOINT}/oauth/custom/authorize`,
        })
        .setBody(body)
        .execute<LoginUserResult>();

    const current = new Date().toISOString();
    const signature = calcSignature(data.Token, current);
    const refreshed = await requestRefreshToken(data.Token.authId, current, signature);
    const refreshToken: LemonOAuthToken = {
        ...refreshed.Token,
        identityToken: data.Token.identityToken,
    };
    return await webCore.buildCredentialsByToken(refreshToken);
};

export const verifyEmail = async (body: VerifyEmailBody) => {
    const { data } = await webCore
        .buildSignedRequest({ method: 'POST', baseURL: `${OAUTH_ENDPOINT}/oauth/0/verify-email` })
        .setBody(body)
        .execute<VerifyEmailView>();

    return data;
};

export const snsTestLogin = async (tokenBody: VerifyNativeTokenBody) => {
    const { data } = await webCore
        .buildSignedRequest({ method: 'POST', baseURL: `${OAUTH_ENDPOINT}/oauth/0/verify-native-token` })
        .setParams({ token: 1 })
        .setBody(tokenBody)
        .execute<LemonRefreshTokenResult>();

    const refreshToken: LemonOAuthToken = {
        ...data.Token,
        identityToken: data.Token.identityToken,
    };

    await webCore.buildCredentialsByToken(refreshToken);

    return data;
};

export const uploadImage = async (
    image: File,
    onUploadProgress?: (event: unknown) => void
): Promise<UploadedImage | null> => {
    if (!image) {
        throw new Error('@image is required');
    }
    const extraConfig = onUploadProgress ? { onUploadProgress } : {};
    const form = new FormData();
    form.append('file', image);

    const { data } = await webCore
        .buildSignedRequest({
            method: 'POST',
            baseURL: `${IMAGE_API_ENDPOINT}/upload`,
        })
        .setParams({})
        .setBody(form)
        .addHeaders({
            Accept: 'application/json, text/plain, */*',
            'Content-Type': 'multipart/form-data',
        })
        .addAxiosRequestConfig(extraConfig)
        .execute();

    try {
        return data?.list[0];
    } catch (e) {
        return null;
    }
};
